import React, { useState } from "react";
import { ViewIcon, HideIcon } from "@shopify/polaris-icons";
import { Button, TextField } from "@shopify/polaris";
import { Formik } from "formik";
import { css, StyleSheet } from "aphrodite";
import mooshooWave from "../../../assets/care-screener/mooshoo-wave-mini.png";
import accentThree from "../../../assets/care-screener/accents-three.png";
import accentOne from "../../../assets/care-screener/accents-one.png";
import ButtonComponent from "../../Button";
import Text from "../../core/Text";
import FormErrorMessage from "../../FormErrorMessage";
import Flex from "../../core/Flex";
import { MANA_BLUE_DARK } from "src/theme/colors";
import { getPasswordError } from "src/utils/formUtils";
import { TouchableOpacity } from "@components/react-native";
import { logEvent } from "../../../actions";
import { useActions } from "tiny-atom/react/hooks";

type Props = {
  onPasswordSubmitted: (value: string | undefined) => Promise<void>;
  showNextLine?: boolean;
  subttitleText: string;
  consultPath: string;
};

export default function CreateYourAccount({ onPasswordSubmitted, subttitleText, showNextLine, consultPath }: Props) {
  const { setConsultCallback, navigate } = useActions();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState<string>();
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const errors: { password?: string } = {};
    const error = getPasswordError(password);
    if (error) {
      errors.password = error;
    }
    return errors;
  };

  const handleSubmit = () => {
    setLoading(true);
    onPasswordSubmitted(password).then(() => {
      setLoading(false);
    });
  };

  return (
    <>
      {showNextLine && (
        <div
          style={{
            borderTop: "2px solid #E0E0E0",
            width: "100%",
            textAlign: "center",
            padding: 10,
            marginTop: 10,
            color: MANA_BLUE_DARK,
            fontWeight: 600,
            fontSize: "1rem"
          }}
        >
          Next
        </div>
      )}
      <Flex>
        <div className={css(styles.card)}>
          <div className={css(styles.row)}>
            <img src={accentOne} className={css(styles.accentOne)} />
            <img src={mooshooWave} className={css(styles.mascot)} />
            <img src={accentThree} className={css(styles.accentThree)} />
            <div>
              <Text tag="span" style={styles.title} color="#2563eb" weight={700} size={18}>
                Create your account
              </Text>
              <Text tag="span" style={styles.subtitle} color="#6b7280" size={13}>
                {subttitleText}
              </Text>
            </div>
          </div>
          <Formik initialValues={{ password: "" }} onSubmit={handleSubmit} validate={validateForm}>
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit} className="createPasswordForm">
                <FormErrorMessage name="password" component="div" />
                <div className={css(styles.inputWrapper)}>
                  <TextField
                    placeholder="password"
                    onChange={setPassword}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    label=""
                    autoComplete="off"
                  />
                  <div className={css(styles.eyeButtonWrapper)}>
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      variant={loading ? "monochromePlain" : "plain"}
                      size="slim"
                      icon={showPassword ? HideIcon : ViewIcon}
                      accessibilityLabel={showPassword ? "Hide password" : "Show password"}
                      loading={loading}
                    />
                  </div>
                </div>
                <ButtonComponent
                  small
                  type="submit"
                  theme="manaBlue"
                  disabled={loading}
                  loading={loading}
                  title="submit"
                  onPress={handleSubmit}
                  style={{ marginTop: 18, width: "100%" }}
                />
              </form>
            )}
          </Formik>
        </div>
      </Flex>
      <p className={css(styles.subtitleGray, styles.font14, styles.marginTop)}>Already have Manatee?</p>
      <p className={css(styles.subtitleGray, styles.font14)}>Enter your existing password.</p>
      <TouchableOpacity
        style={{ cursor: "pointer" }}
        onPress={() => {
          logEvent("careScreenerResetPassword");
          return setConsultCallback({
            params: {
              consultCallback: password => {
                onPasswordSubmitted(password);
                navigate({ pathname: `/${consultPath}` });
              }
            }
          });
        }}
      >
        <Text
          center
          link={{
            onPress: () => {
              logEvent("careScreenerResetPassword");
              return setConsultCallback({
                params: {
                  consultCallback: password => {
                    onPasswordSubmitted(password);
                    navigate({ pathname: `/${consultPath}` });
                  }
                }
              });
            }
          }}
          style={styles.forgotPassword}
        >
          Forgot password?
        </Text>
      </TouchableOpacity>
    </>
  );
}

const styles = StyleSheet.create({
  marginTop: {
    marginTop: 20
  },
  font14: {
    fontSize: 14
  },
  subtitleGray: {
    fontFamily: "Helvetica",
    color: "#636C8C",
    fontSize: 18,
    lineHeight: 1.4,
    fontWeight: 500,
    textAlign: "center"
  },
  forgotPassword: {
    textAlign: "center",
    fontSize: 14,
    lineHeight: 1.4,
    marginTop: 20,
    marginBottom: 10,
    fontWeight: 400
  },
  accentOne: {
    position: "absolute",
    top: -25,
    right: -15,
    zIndex: 50,
    transform: "scaleX(-1) rotate(10deg)"
  },
  accentThree: {
    position: "absolute",
    top: 12,
    left: 10,
    zIndex: 50
  },
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    background: "#f5f8ff",
    paddingTop: 24
  },
  header: {
    marginBottom: 16,
    textAlign: "center"
  },
  card: {
    background: "#eaf2ff",
    borderRadius: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    padding: 24,
    width: 350,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    position: "relative"
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 16,
    gap: 12
  },
  mascot: {
    width: 56,
    height: 56,
    marginRight: 0
  },
  title: {
    marginBottom: 2,
    display: "flex",
    alignItems: "center",
    gap: 4
  },
  subtitle: {
    fontWeight: 400,
    lineHeight: 1.3
  },
  inputWrapper: {
    position: "relative",
    marginTop: 12
  },
  eyeButtonWrapper: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 20,
    height: 24,
    width: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }
});
